import { format } from "date-fns";
import { IConsumptionReading, IConsumptionResult, ILevels } from "../core/contracts/IConsumption";
import addTime, { getFixedTimezoneDate, keepDateFromTimezonePtBr } from "./datetime";
import mean from "lodash/mean";
import { isArrayValid } from "./utility";

export function aggregateConsumptionReadingsDataByDay(
  data?: IConsumptionReading[]
): IConsumptionResult {
  const ret: IConsumptionResult = { readings: [], levels: [] };
  const map: Record<string, IConsumptionReading> = {};
  const levels: Record<string, Record<string, ILevels | undefined>[]>[] = [];

  if (!isArrayValid(data)) {
    return ret as IConsumptionResult;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  data!.forEach((d: IConsumptionReading) => {
    const date: string = format(new Date(d.date), "yyyy-MM-dd");
    if (!map[date]) {
      map[date] = {
        ...d,
        slot: 0,
        date: addTime(new Date(date), new Date().getTimezoneOffset(), "minute").toISOString(),
      };
    } else {
      map[date] = {
        ...map[date],
        level: map[date].level + d.level || 0,
        raw_level: map[date].raw_level + d.raw_level || 0,
      };
    }

    const auxTimestamp = new Date(`${date}T00:00:00`).getTime();
    const dData: string = d.date;

    const { level, raw_level } = d;

    const aux = levels[auxTimestamp]
      ? levels[auxTimestamp]
      : (({} as unknown) as Record<string, Record<string, ILevels | undefined>[]>);
    const auxLevel = {
      ...aux,
      ...{
        [dData]: {
          level: level,
          raw_level: raw_level || 0,
        },
      },
    } as Record<string, Record<string, ILevels | undefined>[]>;

    levels[auxTimestamp] = auxLevel;
  });

  let index = 0;
  for (const date in map) {
    const reading: IConsumptionReading = map[date];
    ret.readings.push(reading);
    ret.levels = levels;
    index++;
  }

  return ret;
}

export function calculateMean(numberArr: number[]) {
  return mean(numberArr);
}

export function calculateVariance(numberArr: number[]) {
  // variance = (sum(xi - mean)^2)/n
  // where mean is the mean of the input array
  // xi is the i-th element of the array
  // n is the length of the array

  // validate input
  if (!(numberArr.length > 0)) {
    return 0;
  }

  // calculate array mean
  const arrMean = calculateMean(numberArr);

  // calculate sum(xi - mean)^2
  let sum = 0;
  for (let ii = 0; ii < numberArr.length; ii++) {
    const element = numberArr[ii];
    sum += (element - arrMean) ** 2;
  }

  // calculate (sum(xi - mean)^2)/n → variance
  const variance = sum / numberArr.length;
  return variance;
}

export function calculateStdDv(numberArr: number[]) {
  const validatedArr = numberArr.filter((x) => x);
  // std dv = sqrt(variance)
  const variance = calculateVariance(validatedArr);
  const stdDev = Math.sqrt(variance);
  return stdDev;
}

export function calculateSum(numberArr: number[]) {
  const validatedArr = numberArr.filter((x) => x);
  // std dv = sqrt(variance)
  const sum = validatedArr.reduce((a, b) => a + b, 0);
  return sum;
}

export function calculateCoefVariation(numberArr: number[]) {
  const validatedArr = numberArr.filter((x) => x);
  // the Coefficient of variation is the define as 100 * ( StdDev/Mean )
  const stdDev = calculateStdDv(validatedArr);
  const mean = calculateMean(validatedArr);

  return 100 * (stdDev / mean);
}
